import type { AppState, DailyWeatherSnapshot, VillageStoryProgress217 } from './models';
import { buildFamilyExpeditionSignals216 } from './familyExpedition216';

const KOREA_OFFSET_MS = 9 * 60 * 60 * 1000;

export type VillageTimePhase217 = 'morning' | 'day' | 'evening' | 'night';
export type MonthlyStoryPhase217 = 'discovery' | 'concept' | 'family' | 'reveal';

export interface MonthlyStorySummary217 {
  monthKey: string;
  title: string;
  phase: MonthlyStoryPhase217;
  phaseLabel: string;
  chapterTitle: string;
  chapterCopy: string;
  progressRate: number;
  acquiredPieces: number;
  totalPieces: number;
  nextActionLabel: string;
  nextActionRoute: string;
  completed: boolean;
}

export interface VillageVisualState217 {
  timePhase: VillageTimePhase217;
  weatherKind: DailyWeatherSnapshot['kind'];
  iglooStage: number;
  iglooTier: number;
  familyFlagVisible: boolean;
  campfireLit: boolean;
  galleryLit: boolean;
  festivalActive: boolean;
  unlockedSpaces: string[];
}

export interface VillageAchievement217 {
  id: string;
  icon: string;
  title: string;
  copy: string;
  createdAt: string;
  route: string;
}

export interface VillageSummary217 {
  version: '2.17';
  generatedAt: string;
  todayStatus: AppState['todayVillage']['status'];
  todayComplete: boolean;
  points: number;
  totalBricks: number;
  iglooStage: number;
  nextIglooRequirement: number;
  puzzleRate: number;
  familySignalsCompleted: number;
  familySignalsTotal: 4;
  familyRewardClaimed: boolean;
  monthlyStory: MonthlyStorySummary217;
  visual: VillageVisualState217;
  achievements: VillageAchievement217[];
}

function koreaClock(date: Date): Date {
  return new Date(date.getTime() + KOREA_OFFSET_MS);
}

export function monthKey217(date = new Date()): string {
  return koreaClock(date).toISOString().slice(0, 7);
}

export function createVillageStoryProgress217(childId: string, date = new Date()): VillageStoryProgress217 {
  const now = date.toISOString();
  return {
    childId,
    monthKey: monthKey217(date),
    acknowledgedAchievementIds: [],
    updatedAt: now,
  };
}

export function normalizeVillageStoryProgress217(
  input: Partial<VillageStoryProgress217> | undefined,
  childId: string,
  date = new Date(),
): VillageStoryProgress217 {
  const base = createVillageStoryProgress217(childId, date);
  if (!input) return base;
  const currentMonth = monthKey217(date);
  return {
    ...base,
    ...input,
    childId,
    monthKey: currentMonth,
    acknowledgedAchievementIds: input.monthKey === currentMonth
      ? Array.from(new Set(input.acknowledgedAchievementIds ?? []))
      : [],
    updatedAt: input.updatedAt || base.updatedAt,
  };
}

export function acknowledgeVillageAchievements217(
  progress: VillageStoryProgress217 | undefined,
  childId: string,
  achievementIds: string[],
  date = new Date(),
): VillageStoryProgress217 {
  const normalized = normalizeVillageStoryProgress217(progress, childId, date);
  return {
    ...normalized,
    acknowledgedAchievementIds: Array.from(new Set([...normalized.acknowledgedAchievementIds, ...achievementIds])),
    lastViewedAt: date.toISOString(),
    updatedAt: date.toISOString(),
  };
}

function storyPhaseFor(date: Date, completed: boolean): MonthlyStoryPhase217 {
  if (completed) return 'reveal';
  const day = koreaClock(date).getUTCDate();
  if (day <= 7) return 'discovery';
  if (day <= 14) return 'concept';
  if (day <= 21) return 'family';
  return 'reveal';
}

export function buildMonthlyStory217(state: AppState, date = new Date()): MonthlyStorySummary217 {
  const puzzle = state.todayVillage.puzzle;
  const completed = puzzle.completed || puzzle.storyUnlocked;
  const phase = storyPhaseFor(date, completed);
  const copy = {
    discovery: {
      label: '1주차 · 사건 발견',
      title: '설옹의 오래된 장부를 발견했어요',
      body: '은행 안쪽에서 오래된 약속과 저축 기록이 담긴 장부가 발견됐어요.',
    },
    concept: {
      label: '2주차 · 경제 개념 탐험',
      title: '은행을 움직이는 힘은 무엇일까요?',
      body: '저축·이자·신뢰가 사람들의 약속을 어떻게 연결하는지 알아봐요.',
    },
    family: {
      label: '3주차 · 가족 선택',
      title: '우리 가족의 작은 저축 약속',
      body: '가족이 함께 지킬 수 있는 작은 목표를 정하고 한 줄로 기록해요.',
    },
    reveal: {
      label: completed ? '4주차 · 이야기 해금' : '4주차 · 마지막 단서',
      title: completed ? '설옹의 비밀 금고가 열렸어요' : '마지막 조각이 이야기를 완성해요',
      body: completed
        ? '완성한 장면과 기억 카드를 퍼즐 갤러리에 오래 보관할 수 있어요.'
        : '남은 조각을 모으면 오래된 은행의 비밀 이야기가 열려요.',
    },
  }[phase];
  const acquiredPieces = puzzle.acquiredPieceIds.length;
  const totalPieces = Math.max(1, puzzle.totalPieces);
  return {
    monthKey: monthKey217(date),
    title: puzzle.title,
    phase,
    phaseLabel: copy.label,
    chapterTitle: copy.title,
    chapterCopy: copy.body,
    progressRate: Math.round((acquiredPieces / totalPieces) * 100),
    acquiredPieces,
    totalPieces,
    nextActionLabel: completed ? '완성 이야기 보기' : '다음 퍼즐 조각 찾기',
    nextActionRoute: '/collection/puzzle',
    completed,
  };
}

function timePhase217(date: Date): VillageTimePhase217 {
  const hour = koreaClock(date).getUTCHours();
  if (hour >= 5 && hour < 11) return 'morning';
  if (hour >= 11 && hour < 17) return 'day';
  if (hour >= 17 && hour < 21) return 'evening';
  return 'night';
}

const FEATURE_SPACE_MAP: Record<string, string> = {
  'basic-storage': '기본 보관함',
  fridge: '냥장고',
  'family-board': '가족 게시판',
  'expedition-room': '탐험 준비실',
  campfire: '캠프파이어 마당',
  'family-missions': '가족 미션 공간',
  'puzzle-gallery': '퍼즐 갤러리',
  'knowledge-vault': '지식 보관소',
  'certificate-hall': '수료증 전시관',
  'village-square': '마을 광장',
  'village-panorama': '스노우빌리지 전망대',
};

export function buildVillageVisualState217(
  state: AppState,
  weather: DailyWeatherSnapshot,
  date = new Date(),
): VillageVisualState217 {
  const signals = buildFamilyExpeditionSignals216(state, state.familyExpedition, date);
  const stage = Math.max(1, Math.min(36, state.todayVillage.igloo.currentStage));
  const unlockedSpaces = state.todayVillage.igloo.unlockedFeatureIds
    .map((id) => FEATURE_SPACE_MAP[id])
    .filter((value): value is string => Boolean(value));
  return {
    timePhase: timePhase217(date),
    weatherKind: weather.kind,
    iglooStage: stage,
    iglooTier: Math.ceil(stage / 6),
    familyFlagVisible: state.familyExpedition.rewardClaimed || state.familyExpedition.completedWeeks.length > 0,
    campfireLit: signals.complete || state.familyExpedition.rewardClaimed,
    galleryLit: state.todayVillage.puzzle.storyUnlocked,
    festivalActive: state.todayVillage.puzzle.completed && stage >= 30,
    unlockedSpaces,
  };
}

export function buildVillageAchievements217(state: AppState, date = new Date()): VillageAchievement217[] {
  const achievements: VillageAchievement217[] = [];
  const today = state.todayVillage;
  if (today.rewardClaimed) {
    achievements.push({
      id: `today-${today.contentDate}`,
      icon: '🧊',
      title: '오늘의 경제 탐험 완료',
      copy: `${today.todayPointsEarned || 23}P와 얼음 벽돌 ${today.todayBricksEarned || 1}개를 보관했어요.`,
      createdAt: today.updatedAt,
      route: '/today/reward',
    });
  }
  const latestPiece = [...today.puzzle.pieceRecords].sort((a, b) => b.acquiredAt.localeCompare(a.acquiredAt))[0];
  if (latestPiece) {
    achievements.push({
      id: `puzzle-${latestPiece.pieceId}-${latestPiece.acquiredAt}`,
      icon: latestPiece.rarity === 'gold' ? '★' : '🧩',
      title: `퍼즐 조각 ${latestPiece.position} 획득`,
      copy: latestPiece.sourceLabel,
      createdAt: latestPiece.acquiredAt,
      route: '/collection/puzzle',
    });
  }
  const latestFamilyWeek = state.familyExpedition.completedWeeks.at(-1);
  if (latestFamilyWeek) {
    achievements.push({
      id: `family-${latestFamilyWeek}`,
      icon: '🏳️',
      title: '가족 탐험대 주간 완주',
      copy: '가족 벽돌과 퍼즐 조각이 마을에 함께 보관됐어요.',
      createdAt: state.familyExpedition.updatedAt,
      route: '/child/family-expedition',
    });
  }
  const milestone = [...today.igloo.completedStages].filter((stage) => stage % 6 === 0).sort((a, b) => b - a)[0];
  if (milestone) {
    achievements.push({
      id: `igloo-${milestone}`,
      icon: '🏠',
      title: `이글루 ${milestone}단계 완성`,
      copy: milestone === 36 ? '스노우빌리지 랜드마크가 완성됐어요.' : '새로운 공간과 기능이 열렸어요.',
      createdAt: today.igloo.updatedAt,
      route: '/village',
    });
  }
  return achievements
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .filter((item, index, list) => list.findIndex((candidate) => candidate.id === item.id) === index)
    .slice(0, 4);
}

export function buildVillageSummary217(
  state: AppState,
  weather: DailyWeatherSnapshot,
  date = new Date(),
): VillageSummary217 {
  const signals = buildFamilyExpeditionSignals216(state, state.familyExpedition, date);
  const puzzle = state.todayVillage.puzzle;
  return {
    version: '2.17',
    generatedAt: date.toISOString(),
    todayStatus: state.todayVillage.status,
    todayComplete: state.todayVillage.rewardClaimed,
    points: state.todayVillage.points,
    totalBricks: state.todayVillage.igloo.totalBricks,
    iglooStage: state.todayVillage.igloo.currentStage,
    nextIglooRequirement: Math.max(0, state.todayVillage.igloo.nextRequirement - state.todayVillage.igloo.totalBricks),
    puzzleRate: Math.round((puzzle.acquiredPieceIds.length / Math.max(1, puzzle.totalPieces)) * 100),
    familySignalsCompleted: signals.completedCount,
    familySignalsTotal: 4,
    familyRewardClaimed: state.familyExpedition.rewardClaimed,
    monthlyStory: buildMonthlyStory217(state, date),
    visual: buildVillageVisualState217(state, weather, date),
    achievements: buildVillageAchievements217(state, date),
  };
}
